import axios from "axios";
import { MessageCircleMore, RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router";

import { useCurrentUser } from "base/app";
import { ChatSession } from "base/ui/chat";
import { DateView } from "base/ui/date";
import { GenericException } from "base/ui/errors";
import { Popup } from "base/ui/popups";
import { LoadingOverlay } from "base/ui/status";
import { URL_REGEX } from "base/ui/utils";
import { useBroadcastedState } from "base/utils/events";
import { idToTitle } from "base/utils";

import { ERP, ORDER_STATUS_DELIVERED } from "../../../../constants";
import { getDisplayPrice, getLabel, getPrice } from "../../../../helpers/StoreHelper";
import IMG_SUCCESS from "../../../../images/onboarding/ic_success.svg?url";
import WarningIcon from "../../../../images/warning.svg?url";
import { EditUserById } from "../../../ui/commonUI";
import ResponsivePopup from "../../../ui/ResponsivePopup";
import CartAndOrderView from "../../store/CartAndOrderView";
import SelectDeliveryUser from "../../store/SelectDeliveryUser";
import useCheckout from "../../store/useCheckout";

const order_status = {
	0: "Received",
	2: "Processing",
	3: "Packed",
	4: "Out for Delivery",
	1: "Delivered",
};
const order_status_keys = [0, 2, 3, 4, 1];

const STATUS_STYLE = {
	0: { backgroundColor: "#F1F1F1", color: "#000" },
	2: { backgroundColor: "#FFE9D5", color: "#CE6000" },
	3: { backgroundColor: "#D4E6FF", color: "#004CBD" },
	4: { backgroundColor: "#15956E26", color: "#15956E" },
	1: { backgroundColor: "#15951A14", color: "#14AA1A" },
};

function calculateTotalQty(items) {
  return Object.values(items).reduce((total, item) => {
    return total + (item?.qty || 0);
  }, 0);
}

function ThirdPartyDetails({ order, setOrder, delivery_route }) {
	const [is_loading, setIsLoading] = useState();
	const to_pay = getPrice(delivery_route.currency, order.total_amount - order.total_paid);
	const delivery_point = delivery_route.delivery_points?.find((d_point) => d_point.name === order.delivery_point_name);
	const disable_thirdparty_sync =
		to_pay > 0 &&
		(delivery_point?.prepayment_percentage
			? delivery_point?.prepayment_percentage == 100
			: delivery_route.prepayment_percentage == 100);

	const syncThirdPartyOrder = () => {
		setIsLoading(true);
		if (order.third_party_platform === "woocommerce") {
			const payload = {
				delivery_route_id: delivery_route._id,
				order_ids: [order._id],
			};
			axios
				.post("/woo/sync/orders/" + delivery_route.delivery_org._id, payload)
				.then((resp) => {
					if (resp.data.errors) {
						GenericException.showPopup(resp.data.errors);
					}
					if (resp.data.orders) {
						setOrder(resp.data.orders?.[0]);
					}
				})
				.finally(() => setIsLoading(false));
		} else if ([ERP.ERPNEXT.id, ERP.MAGENTO.id, ERP.SHOPIFY.id].includes(order.third_party_platform)) {
			axios
				.post(`/api/admin/delivery_route/${delivery_route._id}/order/${order._id}/sync_to_erp`)
				.then((resp) => {
					if (resp.data.errors) {
						GenericException.showPopup(resp.data.errors);
					}
					if (resp.data.order) {
						setOrder(resp.data.order);
					}
				})
				.finally(() => setIsLoading(false));
		}
	};

	return (
		<div className=" tw-text-xs tw-flex tw-justify-between tw-items-center">
			<div className="">
				Created From →
				<span className="tw-font-bold tw-ml-1 tw-text-[#96bf48]">{order.third_party_order_origin || "Sukhiba"}</span>
				{order.third_party_extra_data?.last_synced_at && (
					<div className="tw-text-xxs tw-mt-1">
						Last Sync at:{" "}
						<DateView
							className={`${order.third_party_extra_data.is_sync_failed ? "tw-text-red-600" : "tw-text-green-600"}`}
							millis={order.third_party_extra_data.last_synced_at}
						/>
					</div>
				)}
			</div>
			<div className="tw-justify-end tw-items-center tw-z-20 tw-text-xss">
				{order.third_party_order_id ? <p className="tw-text-end">#{order.third_party_order_id}</p> : null}
				{[ERP.ERPNEXT.id, ERP.MAGENTO.id, "woocommerce", "shopify"].includes(order.third_party_platform) && (
					<div
						className={`tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-border tw-rounded tw-p-1 tw-border-grey tw-mt-1 ${disable_thirdparty_sync && "tw-text-gray-400"}`}
						onClick={(evt) => {
							evt.stopPropagation();
							!disable_thirdparty_sync && syncThirdPartyOrder();
						}}
					>
						<RefreshCcw className={` ${is_loading && "tw-animate-custom-spin"}`} size={14} />
						<span>Sync with {order.third_party_platform === "woocommerce" ? "Woo" : order.third_party_platform}</span>
					</div>
				)}
			</div>
		</div>
	);
}

function LogisticDetails({
	order,
	delivery_route,
	handleUberBooking,
	handleCancelUberBooking,
}) {
	const [is_loading, setIsLoading] = useState();

	return (
		<div className="">
			{delivery_route.has_uber_integration
			? (
				<div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
					<div>
						<p className="tw-text-[13px] tw-font-[600] tw-text-primary-text">Booking</p>
						{order.carrier_order_status ? (
							<div>
								<p
									className={`tw-text-xs tw-mt-0.5 tw-font-[600] ${order.carrier_order_status === "canceled" ? "tw-text-[#DA0A0A]" : "tw-text-[#626F86]"}  tw-capitalize`}
								>
									<span className="tw-text-[#464646]">Uber direct: </span>
									{order.carrier_order_status}
								</p>
								{order.carrier_order_id ? (
									<p className="tw-text-primary-text tw-text-xs tw-mt-0.5">#{order.carrier_order_id}</p>
								) : null}
								{order.carrier_order_data && order.carrier_order_data.delivery_assigned_name ? (
									<>
										<p className="tw-text-[#222222] tw-text-xs tw-mt-0.5 tw-font-[600]">Pickup by -</p>
										<p className="tw-text-[#222222] tw-text-xs tw-mt-0.5 tw-font-[600] tw-ml-1">
											{order.carrier_order_data.delivery_assigned_name}
										</p>
									</>
								) : null}
							</div>
						) : (
							<p className="tw-text-xs tw-text-secondary-text tw-font-[600] ">Book an Uber here</p>
						)}
					</div>
					<div>
						{order.carrier_order_status === "canceled" 
							? (
							<div className="tw-flex tw-flex-col tw-gap-y-1.5">
								<button
									className="tw-text-primary-v2 tw-text-xs tw-font-semibold tw-py-1 tw-px-7 tw-border tw-border-[#D2D2D2] tw-rounded-md"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleUberBooking();
									}}
								>
									Book a Uber
								</button>
								<button
									className="tw-text-primary-text tw-text-xs tw-font-semibold tw-py-1 tw-px-7 tw-border tw-border-[#D2D2D2] tw-rounded-md"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										window.open(order.carrier_order_data.tracking_url);
									}}
								>
									Track Order
								</button>
							</div>
						) : order.carrier_order_status === "delivered" 
							? (
							<button
								className="tw-text-primary-text tw-text-xs tw-font-semibold tw-py-1 tw-px-7 tw-border tw-border-[#D2D2D2] tw-rounded-md"
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									window.open(order.carrier_order_data.tracking_url);
								}}
							>
								Track Order
							</button>
						) : order.carrier_order_status 
							? (
							<div className="tw-flex tw-flex-col tw-gap-y-1.5">
								<button
									className="tw-text-primary-text tw-text-xs tw-font-semibold tw-py-1 tw-px-7 tw-border tw-border-[#D2D2D2] tw-rounded-md"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										window.open(order.carrier_order_data.tracking_url);
									}}
								>
									Track Order
								</button>
								<button
									className="tw-text-[#DA0A0A] tw-text-xs tw-font-semibold tw-py-1 tw-px-7 tw-border tw-border-[#D2D2D2] tw-rounded-md"
									type="button"
									onClick={(e) => {
										var user_confirm_popup = null;
										e.stopPropagation();
										user_confirm_popup = Popup.open(
											"",
											<div className="tw-py-7 tw-flex tw-flex-col tw-justify-center tw-items-center">
												<img src={WarningIcon} alt="warning" width="60px" />
												<div className="tw-text-lg tw-font-semibold tw-p-2 tw-text-center tw-mt-4">
													Are you sure you want to cancel Uber?
												</div>
												<div className="tw-flex tw-flex-row tw-justify-center tw-flex-wrap tw-gap-5 tw-mt-4">
													<button
														type="button"
														className="btn-primary !tw-bg-[#C01616]"
														onClick={() => {
															handleCancelUberBooking();
															user_confirm_popup.close();
														}}
													>
														Cancel Uber
													</button>
												</div>
											</div>,
											{ className: "md:tw-w-[400px]" },
										);
									}}
								>
									Cancel Order
								</button>
							</div>
						) : (
							<button
								className="tw-text-primary-v2 tw-text-xs tw-font-semibold tw-py-1 tw-px-7 	tw-border tw-border-[#D2D2D2] tw-rounded-md"
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleUberBooking();
								}}
							>
								Book a Uber
							</button>
						)}
					</div>
				</div>
			) : (
				<div className="tw-text-xs tw-flex tw-justify-between tw-items-center">
					<div className="">
						<span className="tw-font-bold tw-text-[#96bf48] tw-capitalize">{order.carrier_id || "Flowcart"}</span>
						{order.carrier_order_status ? (
							<div className="tw-text-xxs tw-mt-1">Status: {order.carrier_order_status}</div>
						) : null}
					</div>
					<div className="tw-justify-end tw-items-center tw-z-20 tw-text-xss">
						{order.carrier_order_id ? <p className="tw-text-end">#{order.carrier_order_id}</p> : null}
					</div>
				</div>
			)}
		</div>
	);
}

function OrderDeliveryViewShort({
	order: _order,
	delivery_route,
	refetchOrders,
	className,
	container,
	buying_for_user,
	is_deleted_order,
}) {
	const [cart] = useBroadcastedState("cart_updated");
	const [order, setOrder] = useState(_order);
	const [is_show_order_details, setShowOrderDetails] = useState(false);
	const to_pay = order.total_amount - order.total_paid;
	const user = useCurrentUser();
	const { pathname } = useLocation();
	const is_delivery_route_path = matchPath("/delivery/:delivery_route_id/*", pathname);
	const [is_loading, setIsLoading] = useState();
	const [status, setStatus] = useState(order?.status);
	const shiprazor_warehouse_id = delivery_route?.other_config?.shiprazor_warehouse_id;


	const is_user_manager =
		user?.roles?.superadmin || user?.roles?.staff || user?.roles?.["delivery_org_manager"] || user?.roles?.["manager"];

	/* Checkout Hook */
	useCheckout({ cart, delivery_route, buying_for_user });

	useEffect(() => {
		setStatus(order?.status);
	}, [order?.status]);

	const updateOrder = (_order) => {
		setOrder(_order);
		if (_order.status === ORDER_STATUS_DELIVERED) {
			// TODO: test this
			setShowOrderDetails(false);
		}
	};

	function assignRider() {
		const popup = Popup.show(
			"Assign Rider",
			<SelectDeliveryUser
				delivery_route={delivery_route}
				onDeliveryUserSelected={(user) => {
					assignUserToOrder(user);
					popup.close();
				}}
			/>,
		);
	}

	function assignUserToOrder(user) {
		const order_ids = [order._id];
		const payload = {
			action: "assign_delivery",
			order_ids: order_ids,
		};
		setIsLoading(true);
		axios
			.post(`/api/admin/orders/${delivery_route._id}?delivery_person_id=${user._id}`, payload)
			.then((resp) => {
				if (resp.data.errors) {
					Popup.show("Error", <GenericException ex={resp.data.errors} />);
					return;
				}
				setOrder(resp.data.orders?.[0]);
			})
			.finally(() => setIsLoading(false));
	}

	function assignToLogistics(sync_to) {
		const order_ids = [order._id];
		const payload = {
			order_ids: order_ids,
		};
		setIsLoading(true);
		axios
			.post(`/api/admin/delivery_org/${delivery_route.delivery_org._id}/orders/${sync_to}`, payload)
			.then((resp) => {
				if (resp.data.errors) {
					Popup.show("Error", <GenericException ex={resp.data.errors} />);
					return;
				}
				setOrder(resp.data.orders?.[0])
			})
			.finally(() => setIsLoading(false));
	}

	function orderStatusUpdate(new_status) {
		const payload = {
			action: "change_order_status",
			order_id: order._id,
			order_status: new_status,
		};
		setIsLoading(true);
		axios
			.post(`/api/admin/orders/${delivery_route._id}`, payload)
			.then((resp) => {
				if (resp.data.errors) {
					setStatus(order?.status); // previous status
					if(resp.data.errors.complete_delivery_via_popup){
						setShowOrderDetails(true);
						return;
					};
					Popup.show("Error", <GenericException ex={resp.data.errors} />);
					return;
				}
				setOrder(resp.data.order);
			})
			.finally(() => setIsLoading(false));
	}

	function statusConfirmPopup(status_value) {
		const user_confirm_popup = Popup.open(
			"",
			<div className="tw-p-2">
				<div className="tw-text-lg tw-font-semibold tw-p-2 tw-text-center">Are You Sure ?</div>
				<div className="tw-flex tw-flex-row tw-justify-center tw-flex-wrap tw-gap-5 tw-mt-4">
					<button
						type="button"
						className="btn-primary-v2"
						onClick={() => {
							orderStatusUpdate(status_value);
							user_confirm_popup.close();
						}}
					>
						Yes
					</button>
					<button
						type="button"
						className="btn-secondary"
						onClick={() => {
							setStatus(order?.status);
							user_confirm_popup.close();
						}}
					>
						Cancel
					</button>
				</div>
			</div>,
			{
				cb: (action) => {
					if (action === 0) {
						setStatus(order?.status);
					}
				},
				className: "md:tw-w-[400px]",
			},
		);
	}

	const handleUberBooking = () => {
		assignToLogistics("book_uber");
	}

	const handleCancelUberBooking = () => {
		setIsLoading(true);
		axios
			.post(`/api/admin/delivery_org/${delivery_route.delivery_org._id}/order/${order._id}/uber?action=cancel`)
			.then((resp) => {
				if (resp.data.errors) {
					Popup.show("Error", <GenericException ex={resp.data.errors} />);
					return;
				}
				if(resp.data.is_canceled) {
					Popup.show("", <div className="tw-p-4 tw-grid tw-justify-center tw-text-center tw-justify-items-center tw-gap-1">
						<img src={IMG_SUCCESS} alt="" />
						<div className="tw-font-bold tw-text-md">Uber Canceled Successfully!</div>
					</div>, { className: "md:tw-w-[400px]" });
					setOrder(resp.data.orders?.[0])
				}
			})
			.finally(() => setIsLoading(false));
	}

	const openInGoogleMaps = (gps) => {
		const coordinates = gps?.coordinates;

		if (!Array.isArray(coordinates) || coordinates?.length < 2) {
			return;
		}
		const [long, lat] = gps.coordinates;
		const url = `https://www.google.com/maps?q=${lat},${long}`;
		window.open(url, '_blank');
	};

	const order_user = buying_for_user || order.user;

	return (
		<div className={className || "tw-rounded-lg tw-bg-white tw-drop-shadow tw-shadow-xl tw-mb-2"}>
			{/* user */}
			<div
				className="tw-relative tw-cursor-pointer"
				onClick={() => {
					setShowOrderDetails(true);
				}}
			>
				{is_loading ? <LoadingOverlay title="Please wait" /> : null}
				<div
						className="tw-p-3 tw-flex tw-border-b tw-border-[#DDDDDD] tw-bg-[#F1F1F1] tw-rounded-tl-lg tw-rounded-tr-lg tw-items-center tw-justify-between"
						onClick={async (evt) => {
							evt.stopPropagation();
							const { default: DeliveryRouteUserChatButtons } = await import(
								"../../customers/DeliveryRouteUserChatButtons"
							);
							ChatSession.open(`wcs_${order_user?._id}_${delivery_route.delivery_org.wa_business_number}`, {
								bottomStatusIndicator: DeliveryRouteUserChatButtons.render,
								polling: true,
							})
						}}  
						>
						<div className="tw-flex">
							<div className="tw-w-9 tw-h-9 tw-rounded-full flex-box tw-text-primary-v2 tw-bg-[#15956E0F] tw-mr-2 tw-flex-none">
								<span>{order_user?.name?.slice(0, 1)}</span>
							</div>
							<div>
								<div className="tw-text-xs tw-font-medium">{order_user?.name}</div>
								<div className="tw-text-[11px] tw-text-[#666666] tw-mt-1 tw-font-semibold">#{order._id}</div>
							</div>
						</div>
						<MessageCircleMore size={22} className="tw-text-[#005BD3]" />
				</div>
				<div
					className="tw-py-2 tw-pl-5 tw-grid tw-gap-4"
					style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, max-content))" }}
				>
					<div className="">
						<div className="tw-text-[11px] tw-text-[#999999] tw-font-bold">Phone/Email</div>
						<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">
							{order_user?.phone_number || order_user?.email_id}
						</div>
					</div>
					<div className="">
						<div className="tw-text-[11px] tw-text-[#999999] tw-font-bold">Status</div>
						<div className="tw-text-[13px] tw-text-primary-text tw-mt-1">
							{is_deleted_order ? (
								<div className="tw-text-red-600 tw-text-[13px] tw-font-semibold">Cancelled</div>
							) : is_user_manager ? (
								<div
									className="tw-w-[128px] tw-relative"
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<select
										value={status}
										onChange={(e) => {
											setStatus(Number(e.target.value));
											statusConfirmPopup(e.target.value);
										}}
										style={{ paddingTop: "0.625rem", ...STATUS_STYLE[status] }}
										className="twx-dropdown !tw-py-1 tw-text-xs tw-font-medium tw-appearance-none"
										disabled={status === 1}
									>
										{order_status_keys?.map((status, index) => {
											return (
												<option key={index} value={status} className="tw-text-xs tw-font-medium">
													{order_status[status]}
												</option>
											);
										})}
									</select>
									<svg
										className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-500 tw-pointer-events-none tw-transition-transform tw-duration-200 peer-focus:tw-rotate-180"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M7 10l5 5 5-5" />
									</svg>
								</div>
							) : (
								<div className="tw-text-green-600 tw-text-[13px] tw-font-semibold">{order_status[order.status]}</div>
							)}
						</div>
					</div>
					<div className="">
						<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Products</div>
						<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">{Object.keys(order.items)?.length}</div>
					</div>
					<div className="">
						<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Qty</div>
						<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">{order?.items ? calculateTotalQty(order?.items) : "-"}</div>
					</div>
					<div className="">
						<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Total</div>
						<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">
							{order.total_amount === null ? "-" : getDisplayPrice(delivery_route.currency, order.total_amount)}
						</div>
					</div>
					<div className="">
						<div className="tw-text-xs tw-text-[#999999] tw-font-bold">To Pay</div>
						{
							to_pay > 10 
							? 	<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">
									{getDisplayPrice(delivery_route.currency, to_pay)}
								</div>
							: 	<div className="tw-text-[13px] tw-text-green-500 tw-mt-1 tw-font-semibold">
									{order.total_amount === null ? "-" : "Paid"}
								</div>
						}
						{
							order.data?.forced_payment_message
							? 	<div className="tw-text-[10px] tw-mt-1 tw-font-semibold tw-cursor-text" 
									title={order.data.forced_payment_message}
									onClick={(e) => e.stopPropagation()} 
									dangerouslySetInnerHTML={{
										__html: order.data.forced_payment_message.replace(
											URL_REGEX,
											(url) => `<a href="${url}" target="_blank" class="tw-text-blue-500">Link</a>`
										)
									}} 
								/>
							: null
						}
					</div>
					{
						delivery_route.additional_order_fields?.map(
							(field) => <div key={field} className="">
								<div className="tw-text-xs tw-text-[#999999] tw-font-bold">{idToTitle(field)}</div>
								<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">
									{order.data?.[field] || "-"}
								</div>
							</div>
						)
					}

					{order.purchased_by_user ? (
						<div className="">
							<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Purchased By</div>
							<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">{order.purchased_by_user?.name}</div>
						</div>
					) : null}
					{shiprazor_warehouse_id ? (
						!order.status && !order.carrier_order_id ? (
							<div className="">
								<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Shiprazor</div>
								<div
									className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold"
									onClick={(e) => {
										e.stopPropagation();
										assignToLogistics("sync_to_shiprazor");
									}}
								>
									Click to Send
								</div>
							</div>
						) : null
					) : !is_delivery_route_path && !order.status ? (
						order.delivery_person_id ? (
							<div className="">
								<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Assigned To</div>
								<EditUserById
									className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold"
									user_id={order.delivery_person_id}
									onEdit={() => {
										assignRider();
									}}
								/>
							</div>
						) : (
							<div className="">
								<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Assign Rider</div>
								<div
									className="tw-text-[13px] tw-text-[#005BD3] tw-mt-1 tw-font-semibold tw-underline"
									onClick={(e) => {
										e.stopPropagation();
										assignRider();
									}}
								>
									Click to Assign
								</div>
							</div>
						)
					) : null}
					{order.deleted_by_user ? (
						<div className="">
							<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Cancelled By</div>
							<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">{order.deleted_by_user?.name}</div>
						</div>
					) : null}
					{order.deleted_reason ? (
						<div className="">
							<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Cancelled Reason</div>
							<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-font-semibold">{order.deleted_reason}</div>
						</div>
					) : null}
					{order.origin_of_sale && order.origin_of_sale.length ? (
						<div className="">
							<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Origin of Sale</div>
							<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-capitalize tw-font-semibold">{order.origin_of_sale.join(", ")}</div>
						</div>
					) : null}
					{order.data?.subscription_id ? (
						<div className="">
							<div className="tw-text-xs tw-text-[#999999] tw-font-bold">Type</div>
							<div className="tw-text-[13px] tw-text-primary-text tw-mt-1 tw-capitalize tw-font-semibold tw-bg-[#FFCC00]/50 tw-rounded-full tw-text-center tw-w-fit tw-px-2">Subscription</div>
						</div>
					) : null}
				</div>
				{order.third_party_platform && (
					<div className="tw-p-3 tw-border-t">
						<ThirdPartyDetails order={order} setOrder={setOrder} delivery_route={delivery_route} />
					</div>
				)}
				{!is_deleted_order && (order.carrier_id || (delivery_route.has_uber_integration))
					? (<div className="tw-p-3 tw-border-t">
							<LogisticDetails
								order={order}
								delivery_route={delivery_route}
								handleUberBooking={handleUberBooking}
								handleCancelUberBooking={handleCancelUberBooking}
							/>
						</div>
					)
					: null
				}
				<div className="tw-p-3 tw-border-t tw-text-xs tw-relative w3-flex w3-flex-vcenter">
					<div>
						{order.address ? (
							<div className="tw-text-[#999999] tw-mb-1 tw-text-xs tw-font-[600]">
								<b>Delivery Address: &nbsp;</b>
								<span className="tw-text-primary-v2 tw-underline" onClick={(e) => {
									e.stopPropagation();
									openInGoogleMaps(order?.gps);
								}}>{order.address}</span>
							</div>
						) : null}
						{order.delivery_point_name ? (
							<div className="tw-text-[#999999] tw-mb-1 tw-text-xs tw-font-[600]">
								<b>{getLabel(delivery_route.other_config, "delivery_location", "Delivery Location")}: &nbsp;</b>
								<span className="tw-text-primary-text">{order.delivery_point_name}</span>
							</div>
						) : null}
						{order.created_at ? (
							<div className="tw-text-[#999999] tw-mb-1 tw-text-xs tw-font-[600]">
								<b>Ordered Date: &nbsp;</b>
								<span className="tw-text-primary-text">
									<DateView millis={order.created_at} exclude_time={false} />
								</span>
							</div>
						) : null}
						{order.delivered_time ? (
							<div className="tw-text-[#999999] tw-text-xs tw-font-[600]">
								<b>Delivered Time: &nbsp;</b>
								<span className="tw-text-primary-text">
									<DateView millis={order.delivered_time} />
								</span>
							</div>
						) : null}
						{order.instructions?.delivery ? (
							<div className="tw-text-[#999999] tw-mt-1.5 tw-pt-1.5 tw-border-t tw-border-dashed tw-text-xs tw-font-[600]">
								<b>Delivery Instructions: &nbsp;</b>
								<span className="tw-text-primary-text">{order.instructions?.delivery}</span>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<ResponsivePopup
				onClose={() => setShowOrderDetails(false)}
				show={is_show_order_details}
				title="View Order"
				container={container}
			>
				{order.third_party_platform && (
					<div className="tw-p-3 tw-border-b">
						<ThirdPartyDetails order={order} setOrder={setOrder} delivery_route={delivery_route} />
					</div>
				)}
				<CartAndOrderView
					cart={cart}
					order={order}
					is_deleted_order={is_deleted_order}
					delivery_route={delivery_route}
					user={user}
					buying_for_user={order_user}
					onRefresh={refetchOrders}
					onOrderUpdate={updateOrder}
					container={container}
				/>
			</ResponsivePopup>
		</div>
	);
}

export { OrderDeliveryViewShort };
